import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  forwardRef
} from 'react';
import PropTypes from 'prop-types';
import { useInView } from 'react-intersection-observer';
import Button, { LinkButton } from '@strava/ui/Button';
import clsx from '@strava/ui/clsx';
import {
  useStravaAnalytics,
  useStravaTranslations,
  useStravaBranch
} from '@strava/container-context';

import ActionsCancelNormalXsmall from '@strava/icons/ActionsCancelNormalXsmall';
import { color } from '@strava/ui-tokens/js';
import { MOBILE_OS } from '@strava/constants/src/appConstants';
import { isLocalStorageAvailable } from '@strava/utils/src/utils';
import styles from './SmartBanner.module.scss';

import iOSIcon from './assets/icon-ios-app.svg';
import androidIcon from './assets/icon-android-app.svg';

export const I18N_PREFIX = 'microfrontends.mobile_app_download_banner';
export const HIDE_BANNER_STORAGE_KEY = 'hide-mobile-app-download-banner';

const SmartBanner = forwardRef(
  ({ page, category, mobileOS, showCloseBtn, className }, ref) => {
    const { hasApp, appLink, isProcessing } = useStravaBranch();
    const { track: _track } = useStravaAnalytics();
    const { t: _t } = useStravaTranslations();

    const [inViewRef, inView] = useInView({ threshold: 0 });

    const [hideBanner, setHideBanner] = useState(false);

    const track = useCallback(
      (fields = {}) => {
        _track({
          category,
          page,
          ...fields,
          properties: {
            ...fields.properties
          }
        });
      },
      [_track, category, page]
    );

    const t = useMemo(
      () => (key, params) => _t(`${I18N_PREFIX}.${key}`, { ...params }),
      [_t]
    );

    const getAppInfoCopy = () => {
      if (
        [MOBILE_OS.iPhone, MOBILE_OS.android].includes(mobileOS?.toLowerCase())
      ) {
        return t(`app.${mobileOS.toLowerCase()}`);
      }
      return t('no_app');
    };

    useEffect(() => {
      let hide = false;
      if (isLocalStorageAvailable()) {
        hide = localStorage?.getItem(HIDE_BANNER_STORAGE_KEY) === 'true';
      }

      if (!hide && inView) {
        track({ element: 'smartbanner', action: 'screen_enter' });
      }
      setHideBanner(hide);
    }, [track, inView]);

    const handleCloseBanner = () => {
      track({
        element: 'dismiss_smartbanner',
        action: 'click'
      });
      setHideBanner(true);
      if (isLocalStorageAvailable()) {
        localStorage?.setItem(HIDE_BANNER_STORAGE_KEY, true);
      }

      // allows active to remove placeholder spacing when smartbanner is hidden
      document.dispatchEvent(new Event('removeSmartbanner'));
    };

    if (hideBanner) {
      return null;
    }

    return (
      <div
        ref={ref}
        data-cy="mobile-app-download-banner"
        className={clsx(styles.wrapper, className)}
      >
        <div className={styles.smartBanner} ref={inViewRef}>
          {showCloseBtn && (
            <Button
              variant="icon"
              type="button"
              className={styles.closeBtn}
              onClick={handleCloseBanner}
              aria-label={t('close_banner')}
              data-cy="mobile-app-download-banner-close"
            >
              <ActionsCancelNormalXsmall fill={color.colorCoreO3} />
            </Button>
          )}

          <img
            src={
              mobileOS?.toLowerCase() === MOBILE_OS.android
                ? androidIcon
                : iOSIcon
            }
            alt=""
          />

          <div className={styles.appInfo}>
            <div>Strava</div>
            <div>{getAppInfoCopy()}</div>
          </div>
          <LinkButton
            href={appLink}
            variant="primaryOutline"
            className={styles.btn}
            data-cy="mobile-app-download-banner-cta"
            disabled={isProcessing}
            onClick={() =>
              track({
                element: hasApp ? 'open_app' : 'download_app',
                action: 'click',
                properties: {
                  url: appLink
                }
              })
            }
          >
            {hasApp ? t('open') : t('download')}
          </LinkButton>
        </div>
      </div>
    );
  }
);

SmartBanner.defaultProps = {
  mobileOS: null,
  className: null,
  showCloseBtn: true
};

SmartBanner.propTypes = {
  page: PropTypes.string.isRequired,
  category: PropTypes.string.isRequired,
  mobileOS: PropTypes.string,
  className: PropTypes.string,
  showCloseBtn: PropTypes.bool
};

export default SmartBanner;
